import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconSearch } from '@tabler/icons-react';

import { CancelMonitoredSubjectModal, LogoPaper } from '@/components';
import { useTeams } from '@/hooks';
import { useMonitoredSubjects } from '@/hooks/useMonitoredSubjects';
import {
  CANCELLATION_REASON_LABELS,
  MONITORED_SUBJECT_STATUS_LABELS,
  MonitoredSubjectRow,
  MonitoredSubjectStatus,
  monitoringComponentLabel,
} from '@/types/monitoring';

const PAGE_SIZE = 50;

// Module-level so the Select gets the same array every render.
const STATUS_OPTIONS = Object.entries(MONITORED_SUBJECT_STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const STATUS_COLOURS: Record<MonitoredSubjectStatus, string> = {
  active: 'green',
  pending: 'yellow',
  suspended: 'orange',
  cancelled: 'gray',
};

/**
 * Admin: every monitored subject across every team, and cancelling one.
 *
 * Paging, filtering and name search all run server-side, so a filter change
 * resets to page 1 — staying on page 4 of a narrower set would show an empty
 * table that reads as "nothing monitored".
 */
export function MonitoredSubjectsPage() {
  const { teams } = useTeams();
  const [page, setPage] = useState(1);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [status, setStatus] = useState<MonitoredSubjectStatus | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [cancelling, setCancelling] = useState<MonitoredSubjectRow | null>(null);
  const [registering, setRegistering] = useState<string | null>(null);

  const {
    page: result,
    isLoading,
    error,
    cancel,
    register,
  } = useMonitoredSubjects({
    page,
    pageSize: PAGE_SIZE,
    teamId: teamId ?? undefined,
    status: status ?? undefined,
    q: debouncedSearch.trim() || undefined,
  });

  const teamNames = useMemo(
    () => Object.fromEntries(teams.map((team) => [String(team.id), team.name])),
    [teams]
  );
  const teamOptions = useMemo(
    () =>
      teams
        .map((team) => ({ value: String(team.id), label: team.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [teams]
  );
  const resetPage = () => setPage(1);
  const totalPages = Math.ceil(result.total / PAGE_SIZE);

  const confirmCancel = async (payload: {
    reason: Parameters<typeof cancel>[1]['reason'];
    note: string | null;
  }) => {
    if (!cancelling) {
      return;
    }
    const outcome = await cancel(cancelling.id, payload);
    setCancelling(null);
    if (outcome.complete) {
      notifications.show({
        title: 'Monitoring cancelled',
        message: `${outcome.subject.name} is no longer monitored.`,
        color: 'green',
      });
    } else {
      // Cancelled here, but Castellum has not removed it yet — the one
      // outcome that still costs the client money, so it must not read as done.
      notifications.show({
        title: 'Cancelled, but Castellum still holds it',
        message:
          `${outcome.subject.name} is cancelled and no alerts reach the client, but removal ` +
          `from Castellum failed (${outcome.deregistrationFailures.join('; ')}). It is retried ` +
          'automatically every few minutes.',
        color: 'orange',
        autoClose: false,
      });
    }
  };

  const retryRegistration = async (row: MonitoredSubjectRow) => {
    setRegistering(row.id);
    try {
      const outcome = await register(row.id);
      if (outcome.registered) {
        notifications.show({
          title: 'Registered with Castellum',
          message: `${outcome.subject.name} is now monitored.`,
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Castellum still refused it',
          message:
            `${outcome.subject.name} stays pending: ${outcome.registrationError ?? 'no reason given'}. ` +
            'It is retried automatically every few minutes.',
          color: 'orange',
          autoClose: false,
        });
      }
    } catch (err) {
      notifications.show({
        title: 'Could not retry registration',
        message: err instanceof Error ? err.message : 'The request failed.',
        color: 'red',
      });
    } finally {
      setRegistering(null);
    }
  };

  return (
    <Stack>
      <Container size="xl" w="100%">
        <LogoPaper>
          <Stack>
            <Title order={2}>Monitored subjects</Title>

            <Group align="flex-end">
              <TextInput
                label="Name"
                placeholder="Search subjects…"
                leftSection={<IconSearch size={14} />}
                value={search}
                onChange={(e) => {
                  setSearch(e.currentTarget.value);
                  resetPage();
                }}
                data-testid="monitored-subjects-search"
              />
              <Select
                label="Client"
                placeholder="All clients"
                data={teamOptions}
                value={teamId}
                onChange={(value) => {
                  setTeamId(value);
                  resetPage();
                }}
                searchable
                clearable
              />
              <Select
                label="Status"
                placeholder="Any status"
                data={STATUS_OPTIONS}
                value={status}
                onChange={(value) => {
                  setStatus(value as MonitoredSubjectStatus | null);
                  resetPage();
                }}
                clearable
                data-testid="monitored-subjects-status"
              />
            </Group>

            {error && (
              <Alert color="red" title="Could not load monitored subjects">
                {error.message}
              </Alert>
            )}

            {isLoading && result.items.length === 0 && <Loader size="sm" />}

            {!isLoading && result.items.length === 0 && !error && (
              <Text c="dimmed">No monitored subjects match these filters.</Text>
            )}

            {result.items.length > 0 && (
              <Table.ScrollContainer minWidth={1000}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Subject</Table.Th>
                      <Table.Th>Client</Table.Th>
                      <Table.Th>Checks</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Enrolled</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {result.items.map((row) => (
                      <Table.Tr key={row.id} data-testid={`monitored-subject-${row.id}`}>
                        <Table.Td>
                          <Stack gap={2}>
                            {row.reportId ? (
                              <Anchor component={Link} to={`/reports/${row.reportId}`} size="sm">
                                {row.name}
                              </Anchor>
                            ) : (
                              <Text size="sm">{row.name}</Text>
                            )}
                            {row.nameSource === 'subject' && (
                              <Text size="xs" c="dimmed">
                                Registered under the intake name, not a confirmed match
                              </Text>
                            )}
                          </Stack>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{teamNames[row.teamId] ?? row.teamId}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            {row.scopeComponentSlugs.map((slug) => (
                              <Badge key={slug} size="sm" variant="outline" color="gray">
                                {monitoringComponentLabel(slug)}
                              </Badge>
                            ))}
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Stack gap={2}>
                            <Group gap={4}>
                              <Badge color={STATUS_COLOURS[row.status]} variant="light">
                                {MONITORED_SUBJECT_STATUS_LABELS[row.status]}
                              </Badge>
                              {row.status !== 'active' && row.registeredUpstream && (
                                <Tooltip label="Castellum still holds this subject and may still be billing for it. Removal is retried automatically.">
                                  <Badge color="red" variant="outline" size="sm">
                                    still billed
                                  </Badge>
                                </Tooltip>
                              )}
                            </Group>
                            {row.cancellationReason && (
                              <Text size="xs" c="dimmed">
                                {CANCELLATION_REASON_LABELS[row.cancellationReason]}
                                {row.cancellationNote ? ` — ${row.cancellationNote}` : ''}
                              </Text>
                            )}
                          </Stack>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {row.enrolledAt ? new Date(row.enrolledAt).toLocaleDateString() : '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} justify="flex-end" wrap="nowrap">
                            {/* Not offered when Castellum already holds a pending
                                subject: registering it again would bill twice. */}
                            {row.status === 'pending' && !row.registeredUpstream && (
                              <Button
                                size="xs"
                                variant="subtle"
                                loading={registering === row.id}
                                disabled={registering !== null && registering !== row.id}
                                onClick={() => retryRegistration(row)}
                              >
                                Retry registration
                              </Button>
                            )}
                            {row.status !== 'cancelled' && (
                              <Button
                                size="xs"
                                variant="subtle"
                                color="red"
                                onClick={() => setCancelling(row)}
                              >
                                Cancel
                              </Button>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}

            {totalPages > 1 && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, result.total)} of{' '}
                  {result.total} subjects
                </Text>
                <Pagination value={page} onChange={setPage} total={totalPages} />
              </Group>
            )}
          </Stack>
        </LogoPaper>
      </Container>

      <CancelMonitoredSubjectModal
        opened={cancelling !== null}
        onClose={() => setCancelling(null)}
        subjectName={cancelling?.name ?? ''}
        onConfirm={confirmCancel}
      />
    </Stack>
  );
}
